import React, { useEffect, useRef, useState } from "react";
import { matchPath, useNavigate } from "react-router-dom";
import { authHelper } from "./utils/helpers";
import { Routes } from "./utils/constants";
import { useGlobalState } from "./contexts/GlobalContext";
import { logger } from "./utils/helpers/logger";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * 認証が必要なルートを保護するためのラッパーコンポーネント
 * 
 * 動作仕様：
 * 1. 最初の5秒間、1秒ごとにトークンの存在を確認
 * 2. アクセストークンとリフレッシュトークンの両方がない場合：
 *    - 現在のURLをreturnToパラメータとして保存し、ゲストページへリダイレクト
 * 3. アクセストークンのみない場合：
 *    - リフレッシュトークンを使用して新しいアクセストークンを取得
 * 4. ローディング中は空のコンポーネントを表示し、認証確認後に子コンポーネントをレンダリング
 * 
 * 注意：ポーリングは5秒後に自動停止し、パフォーマンスへの影響を最小化
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { refreshAccessToken } = useGlobalState();
  const navigate = useNavigate();
  const apiCalledRef = useRef(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiCalledRef.current = false;
    const interval = setInterval(() => {
      const refresh_token = authHelper.valueCookie("_rftk");
      const access_token = authHelper.accessToken();
      if (!access_token && !refresh_token && (location.pathname !== Routes.LOGOUT)) {
        const urlParams = new URLSearchParams(window.location.search);
        let returnTo = urlParams.get("returnto");
        const currentPath = [
          Routes.USER,
          Routes.USERGUEST,
        ];
  
        const isPath= currentPath.some((path) =>
          matchPath(path, location.pathname)
        );
        if(!returnTo && !isPath) {
          returnTo = window.location.href
        }
        
        if(returnTo) {
          navigate(Routes.USERGUEST + '?returnto=' + encodeURIComponent(returnTo));
        } else {
          logger("[ProtectedRoute]", "Navigating to USERGUEST without returnTo");
          navigate(Routes.USERGUEST);
        }
      } else if (!access_token && refresh_token && !apiCalledRef.current) {
        apiCalledRef.current = true;
        refreshAccessToken();
      }
      setLoading(false)
    }, 1000);
    setTimeout(() => {
      clearInterval(interval);
    }, 5000);
    return () => clearInterval(interval);
  }, [location.pathname, navigate, apiCalledRef]);

  if (loading) {
    return <></>;
  }
  return <>{children}</>;
};

export default ProtectedRoute;
